// Receivable Income Management Page - Redesigned to match Income page UI
// Only visible when hiddenMode is ON
// Manages income from charges, terrace, and other work amounts

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaEdit,
  FaEye,
  FaFilter,
  FaPlus,
  FaSearch,
  FaTrash,
} from "react-icons/fa";
import CompactActionBar from "../components/CompactActionBar";
import { parseUnitSearch } from "../utils/unitSearch";
import ConfirmDialog from "../components/ConfirmDialog";
import { useAuth } from "../contexts/AuthContext";
import { useHiddenMode } from "../contexts/HiddenModeContext";
import "../css/pages/Income.css";
import {
  hiddenIncomeService,
  type HiddenIncome as HiddenIncomeType,
  type BookingWithHiddenIncome,
  type CreateHiddenIncomeData,
} from "../services/hiddenIncomeService";
import api from "../services/api";

interface HiddenIncomeProps {
  initialFilterType?: string; // Optional prop to pre-filter by income type (e.g., "banakhat")
}

const HiddenIncome: React.FC<HiddenIncomeProps> = ({ initialFilterType }) => {
  const { user } = useAuth();
  const { isHiddenModeActive } = useHiddenMode();
  const navigate = useNavigate();
  
  // State for income list
  const [hiddenIncomes, setHiddenIncomes] = useState<HiddenIncomeType[]>([]);
  const [decryptedData, setDecryptedData] = useState<Map<string, { amount: string; incomeDate: string; collectedAmount: string }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [total, setTotal] = useState(0);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterUnit, setFilterUnit] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterIncomeType, setFilterIncomeType] = useState("");
  const [filterApprovalStatus, setFilterApprovalStatus] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterProject, setFilterProject] = useState("");
  
  // Projects list for dropdown
  const [projects, setProjects] = useState<Array<{ id: number; name: string }>>([]);
  
  // Applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    unit: "",
    customer: "",
    mobile: "",
    incomeType: "",
    approvalStatus: "",
    dateFrom: "",
    dateTo: "",
    projectId: "",
  });

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState<HiddenIncomeType | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [incomeToDelete, setIncomeToDelete] = useState<HiddenIncomeType | null>(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    incomeType: "",
    amount: 0,
  });

  useEffect(() => {
    loadProjects();
    // Set initial filter if provided (e.g., for banakhat page)
    if (initialFilterType) {
      setFilterIncomeType(initialFilterType);
      setAppliedFilters(prev => ({ ...prev, incomeType: initialFilterType }));
    }
  }, [initialFilterType]);

  useEffect(() => {
    if (isHiddenModeActive) {
      loadHiddenIncomes();
    }
  }, [isHiddenModeActive, currentPage, limit, appliedFilters]);

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      // API returns array directly or wrapped in data
      const projectsData = Array.isArray(response.data) ? response.data : response.data.data;
      if (projectsData && projectsData.length > 0) {
        setProjects(projectsData);
        console.log('Loaded projects:', projectsData);
        
        // Auto-select first project
        const firstProjectId = projectsData[0].id.toString();
        setFilterProject(firstProjectId);
        setAppliedFilters(prev => ({ ...prev, projectId: firstProjectId }));
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadHiddenIncomes = async () => {
    setLoading(true);
    try {
      const response = await hiddenIncomeService.getHiddenIncomes({
        page: currentPage,
        limit: limit,
        search: appliedFilters.customer || appliedFilters.mobile || appliedFilters.unit || undefined,
        incomeType: appliedFilters.incomeType || undefined,
        projectId: appliedFilters.projectId ? parseInt(appliedFilters.projectId) : undefined,
      });
      
      const incomes = response.data;
      console.log('Hidden incomes received:', incomes.length, 'records');
      console.log('Sample income with project:', incomes[0]);
      
      // Apply frontend filters
      let filtered = incomes;
      
      // Customer name filter
      if (appliedFilters.customer) {
        const searchLower = appliedFilters.customer.toLowerCase();
        filtered = filtered.filter((income: HiddenIncomeType) =>
          income.customerName?.toLowerCase().includes(searchLower)
        );
      }
      
      // Mobile filter
      if (appliedFilters.mobile) {
        const searchLower = appliedFilters.mobile.toLowerCase();
        filtered = filtered.filter((income: HiddenIncomeType) =>
          income.customerName?.toLowerCase().includes(searchLower)
        );
      }
      
      // Unit filter - use exact match logic
      if (appliedFilters.unit) {
        const criteria = parseUnitSearch(appliedFilters.unit);
        
        filtered = filtered.filter((income: HiddenIncomeType) => {
          const unitRef = income.unitRef || '';
          
          // Parse unitRef into wing and unitNo
          // unitRef format can be "C-301" or "301"
          let incomeWing: string | null = null;
          let incomeUnitNo: string | null = null;
          
          if (unitRef.includes('-')) {
            const parts = unitRef.split('-');
            incomeWing = parts[0]?.toUpperCase() || null;
            incomeUnitNo = parts[1] || null;
          } else if (unitRef) {
            incomeUnitNo = unitRef;
          }
          
          // Exact match logic
          if (criteria.wing && criteria.unitNo) {
            // "C-301" -> must match wing="C" AND unitNo="301"
            return incomeWing === criteria.wing && incomeUnitNo === criteria.unitNo;
          } else if (criteria.unitNo && !criteria.wing) {
            // "301" -> must match unitNo="301" AND NO wing
            return incomeUnitNo === criteria.unitNo && !incomeWing;
          }
          
          return false;
        });
      }
      
      if (appliedFilters.incomeType && appliedFilters.incomeType !== '') {
        filtered = filtered.filter((income: HiddenIncomeType) => income.incomeType === appliedFilters.incomeType);
      }
      
      if (appliedFilters.approvalStatus && appliedFilters.approvalStatus !== '') {
        const isApproved = appliedFilters.approvalStatus === "approved";
        filtered = filtered.filter((income: HiddenIncomeType) => income.isApproved === isApproved);
      }
      
      setTotal(response.pagination.total);
      setHiddenIncomes(filtered);
      
      // Decrypt amounts for visible records
      if (filtered.length > 0) {
        const ids = filtered.map(income => income.id);
        try {
          const decrypted = await hiddenIncomeService.decryptAmountsBulk(ids);
          const decryptMap = new Map();
          decrypted.forEach((item: any) => {
            decryptMap.set(item.id, {
              amount: item.amount || '0',
              incomeDate: item.incomeDate || '',
              collectedAmount: item.collectedAmount || '0',
            });
          });
          setDecryptedData(decryptMap);
        } catch (error) {
          console.error("Decryption failed:", error);
        }
      }
    } catch (error) {
      console.error("Failed to load hidden incomes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      unit: filterUnit,
      customer: filterCustomer,
      mobile: filterMobile,
      incomeType: filterIncomeType,
      approvalStatus: filterApprovalStatus,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
      projectId: filterProject,
    });
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearAllFilters = () => {
    setFilterUnit("");
    setFilterCustomer("");
    setFilterMobile("");
    setFilterIncomeType("");
    setFilterApprovalStatus("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterProject("");
    setAppliedFilters({
      unit: "",
      customer: "",
      mobile: "",
      incomeType: "",
      approvalStatus: "",
      dateFrom: "",
      dateTo: "",
      projectId: "",
    });
    setCurrentPage(1);
  };

  const handleEdit = (income: HiddenIncomeType) => {
    setEditingIncome(income);
    setEditForm({
      incomeType: income.incomeType,
      amount: parseFloat(income.amount) || 0,
    });
    setShowEditModal(true);
  };

  const handleDelete = (income: HiddenIncomeType) => {
    setIncomeToDelete(income);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (incomeToDelete) {
      try {
        await hiddenIncomeService.deleteHiddenIncome(incomeToDelete.id);
        setShowDeleteDialog(false);
        setIncomeToDelete(null);
        loadHiddenIncomes();
      } catch (error: any) {
        console.error("Error deleting hidden income:", error);
        const errorMessage = error.response?.data?.message || "Failed to delete hidden income";
        alert(errorMessage);
        setShowDeleteDialog(false);
        setIncomeToDelete(null);
      }
    }
  };

  const handleUpdateIncome = async () => {
    if (!editingIncome) return;

    try {
      await hiddenIncomeService.updateHiddenIncome(editingIncome.id, {
        incomeType: editForm.incomeType,
        amount: editingIncome.amount.toString(),
      });
      alert("Hidden income updated successfully");
      setShowEditModal(false);
      setEditingIncome(null);
      loadHiddenIncomes();
    } catch (error) {
      console.error("Update error:", error);
      alert("Failed to update hidden income");
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '' || dateString === 'Invalid Date') return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const getDecryptedAmount = (incomeId: string): string => {
    const data = decryptedData.get(incomeId);
    return data?.amount || '0';
  };

  const getDecryptedDate = (incomeId: string): string => {
    const data = decryptedData.get(incomeId);
    return data?.incomeDate || '';
  };

  const getIncomeTypeLabel = (type: string): string => {
    switch (type) {
      case "charges":
        return "Charges";
      case "terrace":
        return "Terrace";
      case "other_work":
        return "Other Work";
      case "adani_gas":
        return "Adani Gas";
      default:
        return type;
    }
  };

  const getIncomeTypeColor = (type: string): string => {
    switch (type) {
      case "charges":
        return "#e74c3c";
      case "terrace":
        return "#3498db";
      case "other_work":
        return "#f39c12";
      case "adani_gas":
        return "#27ae60";
      default:
        return "#95a5a6";
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (!isHiddenModeActive) {
    return (
      <div className="income-page">
        <div className="error-message">
          <p>⚠️ Hidden mode must be active to view this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="income-page">
      <CompactActionBar>
        <div className="action-bar-left">
          <button
            className={`btn ${showFilters ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setShowFilters(!showFilters)}
            style={{ padding: '6px 12px', fontSize: '13px' }}
          >
            <FaFilter /> Filters
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Status:</span>
            <select
              value={filterApprovalStatus}
              onChange={(e) => {
                setFilterApprovalStatus(e.target.value);
                setAppliedFilters(prev => ({ ...prev, approvalStatus: e.target.value }));
                setCurrentPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Project:</span>
            <select
              value={filterProject}
              onChange={(e) => {
                setFilterProject(e.target.value);
                setAppliedFilters(prev => ({ ...prev, projectId: e.target.value }));
                setCurrentPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
                minWidth: "120px",
              }}
            >
              <option value="">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id.toString()}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Type:</span>
            <select
              value={filterIncomeType}
              onChange={(e) => {
                setFilterIncomeType(e.target.value);
                setAppliedFilters(prev => ({ ...prev, incomeType: e.target.value }));
                setCurrentPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value="">All Types</option>
              <option value="charges">Charges</option>
              <option value="terrace">Terrace</option>
              <option value="other_work">Other Work</option>
              <option value="receivables">Receivables</option>
              <option value="banakhat">Banakhat</option>
              <option value="adani_gas">Adani Gas</option>
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Show:</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>

        <div className="action-bar-right">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/add-receivable-income')}
            style={{ padding: '6px 12px', fontSize: '13px' }}
          >
            <FaPlus /> Add Receivable Income
          </button>
        </div>
      </CompactActionBar>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '8px',
          marginBottom: '16px'
        }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Unit Number
              </label>
              <input
                type="text"
                value={filterUnit}
                onChange={(e) => setFilterUnit(e.target.value)}
                placeholder="Search by unit..."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Customer Name
              </label>
              <input
                type="text"
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                placeholder="Search by name..."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Mobile Number
              </label>
              <input
                type="text"
                value={filterMobile}
                onChange={(e) => setFilterMobile(e.target.value)}
                placeholder="Search by mobile..."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>
          
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button
              onClick={handleApplyFilters}
              className="btn btn-primary"
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              Apply Filters
            </button>
            <button
              onClick={clearAllFilters}
              className="btn btn-secondary"
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      <div className="content-section">
        <table className="standard-table slim">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Customer</th>
              <th>Unit</th>
              <th>Project</th>
              <th>Income Type</th>
              <th>Amount</th>
              <th>Income Date</th>
              <th>Authorized</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center", padding: "20px" }}>
                  Loading...
                </td>
              </tr>
            ) : hiddenIncomes.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center", padding: "20px" }}>
                  No hidden income records found
                </td>
              </tr>
            ) : (
              hiddenIncomes.map((income, index) => (
                <tr key={income.id}>
                  <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                    {total - ((currentPage - 1) * limit) - index}
                  </td>
                  <td style={{ textAlign: "left" }}>
                    <div className="customer-info">
                      {income.customerName || "N/A"}
                    </div>
                  </td>
                  <td>
                    <span className="unit-number">
                      {income.unitRef || "N/A"}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                      {income.project?.name || "-"}
                    </span>
                  </td>
                  <td>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "500",
                        backgroundColor: getIncomeTypeColor(income.incomeType) + "20",
                        color: getIncomeTypeColor(income.incomeType),
                      }}
                    >
                      {getIncomeTypeLabel(income.incomeType)}
                    </span>
                  </td>
                  <td className="amount-cell" style={{ textAlign: 'right' }}>
                    <span className="amount" style={{ color: '#059669', fontSize: '14px', fontWeight: '500' }}>
                      {formatCurrency(parseFloat(getDecryptedAmount(income.id)))}
                    </span>
                  </td>
                  <td>
                    <span className="payment-date">
                      {formatDate(getDecryptedDate(income.id))}
                    </span>
                  </td>
                  <td>
                    {income.approvedAt && !String(income.approvedAt).startsWith("[") ? (
                      <div style={{ fontSize: "13px", color: "#059669" }}>
                        {formatDate(income.approvedAt)}
                      </div>
                    ) : (
                      <span style={{ color: "#6b7280", fontSize: "12px" }}>
                        -
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "500",
                        backgroundColor: income.isApproved ? "#d1fae5" : "#fef3c7",
                        color: income.isApproved ? "#065f46" : "#92400e",
                      }}
                    >
                      {income.isApproved ? "Approved" : "Pending"}
                    </span>
                  </td>
                  <td>
                    {(!income.isApproved || user?.isSuperAdmin) && (
                      <div className="income-actions">
                        <button
                          className="btn-icon edit"
                          onClick={() => handleEdit(income)}
                          title="Edit Hidden Income"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn-icon delete"
                          onClick={() => handleDelete(income)}
                          title="Delete Hidden Income"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && hiddenIncomes.length > 0 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages} ({total} total records)
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingIncome && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Hidden Income</h2>
              <button
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Income Type</label>
                <select
                  value={editForm.incomeType}
                  onChange={(e) =>
                    setEditForm({ ...editForm, incomeType: e.target.value })
                  }
                  className="form-control"
                >
                  <option value="charges">Charges</option>
                  <option value="terrace">Terrace</option>
                  <option value="other_work">Other Work</option>
                  <option value="receivables">Receivables</option>
                  <option value="banakhat">Banakhat</option>
                  <option value="adani_gas">Adani Gas</option>
                </select>
              </div>
              <div className="form-group">
                <label>Amount</label>
                <input
                  type="text"
                  value={formatCurrency(editingIncome.amount)}
                  disabled
                  className="form-control"
                  style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                />
                <small style={{ color: "#6b7280" }}>
                  Amount cannot be changed
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleUpdateIncome} className="btn btn-primary">
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Hidden Income"
        message="Are you sure you want to delete this hidden income record? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setIncomeToDelete(null);
        }}
      />
    </div>
  );
};

export default HiddenIncome;
